// ── Activity Types ─────────────────────────────────────────────────────────────
//
// Domain types and API contracts — what clients send (ActivityCreate) and receive (Activity).
// No Mongoose, no DB details. Those live in activity.model.ts.

import { z } from "zod";
import { Board } from "./board.type";
import { Card } from "./card.type";

// ── ActivityType ───────────────────────────────────────────────────────────────

export const ActivityType = {
  // ── Boards ────────────────────────────────────────────────────────────────────
  BOARD_CREATED: "board_created",
  BOARD_UPDATED: "board_updated",
  BOARD_DELETED: "board_deleted",
  BOARD_RECYCLE_BIN_EMPTIED: "board_recycle_bin_emptied",

  // ── Cards ─────────────────────────────────────────────────────────────────────
  CARD_CREATED: "card_created",
  CARD_DELETED: "card_deleted",
  CARD_CLONED: "card_cloned",
  CARD_RESTORED: "card_restored",
  CARD_COLUMN_CHANGED: "card_column_changed",
  CARD_STATUS_CHANGED: "card_status_changed",
  CARD_PRIORITY_CHANGED: "card_priority_changed",
  CARD_TITLE_CHANGED: "card_title_changed",
  CARD_DESCRIPTION_CHANGED: "card_description_changed",
  CARD_RESULT_CHANGED: "card_result_changed",
  CARD_RESULT_PREVIEW: "card_result_preview",
  CARD_AGENT_ADDED: "card_agent_added",
  CARD_AGENT_REMOVED: "card_agent_removed",
  CARD_TAGS_CHANGED: "card_tags_changed",
  CARD_MEMBER_ADDED: "card_member_added",
  CARD_MEMBER_REMOVED: "card_member_removed",
  CARD_COMMENT_ADDED: "card_comment_added",
  CARD_COMMENT_UPDATED: "card_comment_updated",
  CARD_COMMENT_DELETED: "card_comment_deleted",
  CARD_COMMENT_REPLY_ADDED: "card_comment_reply_added",
  CARD_TASK_STARTED: "card_task_started",
  CARD_TASK_COMPLETED: "card_task_completed",
  CARD_TASK_BLOCKED: "card_task_blocked",
  CARD_ATTACHMENT_ADDED: "card_attachment_added",
  CARD_ATTACHMENT_REMOVED: "card_attachment_removed",
  CARD_UPDATED: "card_updated",
  EVALUATION_ADDED: "evaluation_added",

  // ── Card Sessions ─────────────────────────────────────────────────────────────
  CARD_SESSION_CREATED: "card_session_created",
  CARD_SESSION_UPDATED: "card_session_updated",
  CARD_SESSION_DELETED: "card_session_deleted",
  CARD_SESSION_DATA_ADDED: "card_session_data_added",
  CARD_SESSION_DATA_UPDATED: "card_session_data_updated",
  CARD_SESSION_DATA_DELETED: "card_session_data_deleted",

  // ── Agents ────────────────────────────────────────────────────────────────────
  AGENT_CREATED: "agent_created",
  AGENT_UPDATED: "agent_updated",
  AGENT_DELETED: "agent_deleted",
  AGENT_STATUS_UPDATED: "agent_status_updated",
  AGENT_STATUS_OBSERVATION: "agent_status_observation",
  AGENT_STATUS_THINKING_END: "agent_status_thinking_end",
  AGENT_TASK_DOING: "agent_task_doing",
  AGENT_TASK_WAITING: "agent_task_waiting",
  AGENT_TASK_BLOCKED: "agent_task_blocked",
  AGENT_TASK_COMPLETED: "agent_task_completed",
  AGENT_TASK_DONE: "agent_task_done",
  AGENT_TASK_CANCELLED: "agent_task_cancelled",
  AGENT_TASK_FAILED: "agent_task_failed",
  AGENT_REGISTER_APPROVAL: "agent_register_approval",
  AGENT_REGISTER_REJECTED: "agent_register_rejected",
  AGENT_RESPOND_FEEDBACK: "agent_respond_feedback",
  AGENT_FEEDBACK_CREATED: "agent_feedback_created",
  AGENT_FEEDBACK_UPDATED: "agent_feedback_updated",
  AGENT_FEEDBACK_DELETED: "agent_feedback_deleted",
  AGENT_SUPERVISOR_FEEDBACK_CREATED: "agent_supervisor_feedback_created",
  AGENT_SUPERVISOR_FEEDBACK_UPDATED: "agent_supervisor_feedback_updated",
  AGENT_SUPERVISOR_FEEDBACK_DELETED: "agent_supervisor_feedback_deleted",

  // ── Teams ─────────────────────────────────────────────────────────────────────
  TEAM_CREATED: "team_created",
  TEAM_UPDATED: "team_updated",
  TEAM_DELETED: "team_deleted",
  TEAM_MEMBER_ADDED: "team_member_added",
  TEAM_MEMBER_INVITED: "team_member_invited",
  TEAM_MEMBER_UPDATED: "team_member_updated",
  TEAM_MEMBER_REMOVED: "team_member_removed",

  // ── Resources ─────────────────────────────────────────────────────────────────
  RESOURCE_CREATED: "resource_created",
  RESOURCE_UPDATED: "resource_updated",
  RESOURCE_DELETED: "resource_deleted",

  // ── Positions ─────────────────────────────────────────────────────────────────
  POSITION_CREATED: "position_created",
  POSITION_UPDATED: "position_updated",
  POSITION_DELETED: "position_deleted",

  // ── Apps ──────────────────────────────────────────────────────────────────────
  APP_CREATED: "app_created",
  APP_UPDATED: "app_updated",
  APP_DELETED: "app_deleted",

  // ── App Settings ──────────────────────────────────────────────────────────────
  APP_SETTINGS_CREATED: "app_settings_created",
  APP_SETTINGS_UPDATED: "app_settings_updated",
  APP_SETTINGS_DELETED: "app_settings_deleted",
  APP_SETTINGS_SECTION_REPLACED: "app_settings_section_replaced",
  APP_SETTINGS_SECTION_MERGED: "app_settings_section_merged",
  APP_SETTINGS_SECTION_DELETED: "app_settings_section_deleted",
  APP_SETTINGS_SECTIONS_CLEARED: "app_settings_sections_cleared",

  // ── External Channels ─────────────────────────────────────────────────────────
  EXTERNAL_CHANNEL_CREATED: "external_channel_created",
  EXTERNAL_CHANNEL_UPDATED: "external_channel_updated",
  EXTERNAL_CHANNEL_DELETED: "external_channel_deleted",

  // ── Threads ───────────────────────────────────────────────────────────────────
  THREAD_USER_MESSAGE: "thread_user_message",
  THREAD_AGENT_MESSAGE: "thread_agent_message",
  THREAD_AGENT_MESSAGE_EVALUATION: "thread_agent_message_evaluation",
  THREAD_FEEDBACK: "thread_feedback",
  THREAD_STEP_COSTS: "thread_step_costs",

  // ── A2A Task Status ───────────────────────────────────────────────────────────
  A2A_TASK_STATUS_SUBMITTED: "submitted",
  A2A_TASK_STATUS_WORKING: "working",
  A2A_TASK_STATUS_INPUT_REQUIRED: "required",
  A2A_TASK_STATUS_COMPLETED: "completed",
  A2A_TASK_STATUS_CANCELED: "canceled",
  A2A_TASK_STATUS_FAILED: "failed",
  A2A_TASK_STATUS_REJECTED: "rejected",
  A2A_TASK_STATUS_AUTH_REQUIRED: "auth-required",
  A2A_TASK_STATUS_UNKNOWN: "unknown",

  // ── Results ───────────────────────────────────────────────────────────────────
  RESULT_APPROVED: "result_approved",
  RESULT_REJECTED: "result_rejected",
  RESULT_FEEDBACK: "result_feedback",

  // ── Email ─────────────────────────────────────────────────────────────────────
  EMAIL_RECEIVED: "email_received",
  EMAIL_SENT: "email_sent",
  EMAIL_OPENED: "email_opened",
  EMAIL_CLICKED: "email_clicked",
  EMAIL_BOUNCED: "email_bounced",
  EMAIL_SPAM: "email_spam",

  // ── System ────────────────────────────────────────────────────────────────────
  AI_USAGE: "ai_usage",
  SYSTEM_TRACE: "system_trace",
  NOTIFICATION: "notification",
  RESOURCE_PUBLISHED: "resource_published",

  // ── Profiles ──────────────────────────────────────────────────────────────────
  USER_SIGNUP: "user_signup",
  USER_PROFILE_UPDATED: "user_profile_updated",
  USER_PROFILE_DELETED: "user_profile_deleted",

  // ── Integrations ──────────────────────────────────────────────────────────────
  INTEGRATION_CREATED: "integration_created",
  INTEGRATION_UPDATED: "integration_updated",
  INTEGRATION_DELETED: "integration_deleted",

  // ── Agent Retire ──────────────────────────────────────────────────────────────
  AGENT_RETIRED: "agent_retired",

  // ── Agent Drafts ──────────────────────────────────────────────────────────────
  AGENT_DRAFT_CREATED: "agent_draft_created",
  AGENT_DRAFT_UPDATED: "agent_draft_updated",
  AGENT_DRAFT_DELETED: "agent_draft_deleted",
  AGENT_DRAFT_PUBLISHED: "agent_draft_published",

  // ── Notifications ─────────────────────────────────────────────────────────────
  NOTIFICATION_CREATED: "notification_created",

  // ── Benchmarks ────────────────────────────────────────────────────────────────
  BENCHMARK_CREATED: "benchmark_created",
  BENCHMARK_UPDATED: "benchmark_updated",
  BENCHMARK_DELETED: "benchmark_deleted",
  BENCHMARK_EXECUTION_CREATED: "benchmark_execution_created",
  BENCHMARK_EXECUTION_STARTED: "benchmark_execution_started",
  BENCHMARK_EXECUTION_UPDATED: "benchmark_execution_updated",
  BENCHMARK_EXECUTION_DELETED: "benchmark_execution_deleted",
} as const;

// ── ActorType ──────────────────────────────────────────────────────────────────

export const ActorType = {
  USER: "user",
  AGENT: "agent",
  SYSTEM: "system",
} as const;

export type ActorType = (typeof ActorType)[keyof typeof ActorType];

// ── Actor ──────────────────────────────────────────────────────────────────────

export const Actor = z.object({
  id: z.string().min(1).describe("Actor identifier"),
  type: z.enum(Object.values(ActorType) as [string, ...string[]]).describe("Actor type"),
  name: z.string().min(1).describe("Actor display name"),
});
export type Actor = z.infer<typeof Actor>;

// ── ActivityChange ─────────────────────────────────────────────────────────────

export const ActivityChange = z.object({
  field: z.string().min(1).describe("Changed field name"),
  old_value: z.unknown().optional().describe("Value before the change"),
  new_value: z.unknown().optional().describe("Value after the change"),
});
export type ActivityChange = z.infer<typeof ActivityChange>;

// ── Response DTO — Activity ────────────────────────────────────────────────────

export const Activity = z.object({
  id: z.string().describe("Unique resource identifier"),
  board_id: z.string().min(1).optional().describe("Parent board ID"),
  card_id: z.string().min(1).optional().describe("Related card ID"),
  agent_id: z.string().min(1).optional().describe("Related agent ID"),
  app_id: z.string().min(1).optional().describe("Related app ID"),
  notification_id: z.string().min(1).optional().describe("Related notification ID"),
  type: z
    .enum(Object.values(ActivityType) as [string, ...string[]])
    .describe("Activity event type"),
  description: z.string().min(1).describe("Human-readable activity description"),
  actor: Actor.partial().optional().describe("Actor who performed the action"),
  team_id: z.string().min(1).describe("Owning team ID"),
  changes: z.array(ActivityChange.partial()).optional().describe("List of field-level changes"),
  created_by_session_id: z.string().optional().describe("Session that created this activity"),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Arbitrary additional metadata"),
  user_id: z.string().optional().describe("User ID"),
  marked_for_display: z.boolean().optional().describe("Whether to show in UI feed"),
  created_at: z.date().describe("Creation timestamp"),
  // ── Computed embed fields — present only when ?embed= is requested ──────────
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=board. */
  board: Board.partial().optional().describe("Embedded board — present only when ?embed=board"),
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=card. */
  card: Card.partial().optional().describe("Embedded card — present only when ?embed=card"),
});

export type Activity = z.infer<typeof Activity>;

// ── Input DTOs ─────────────────────────────────────────────────────────────────

// actor is already optional in Activity — no .extend() needed
export const ActivityCreate = Activity.omit({
  id: true,
  created_at: true,
  // embed fields — computed by the repo, never sent by the client
  board: true,
  card: true,
});

export type ActivityCreate = z.infer<typeof ActivityCreate>;
